import React, { useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { useSession } from "../../../../context/SessionContext";
import { getDemandNote, decideDemandNote, cancelDemandNoteRemainder } from "./demandNoteService";

const DemandNoteDetailsModal = ({ open, onClose, note: noteProp, onChanged }) => {
  const { session } = useSession();
  const [note, setNote] = useState(noteProp);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelQty, setCancelQty] = useState({});

  useEffect(() => {
    setNote(noteProp);
    setComments("");
    setShowCancel(false);
  }, [noteProp]);

  if (!open || !note) {
    return null;
  }

  const priorityType =
    note.Priority === "High / Urgent" || note.Priority === "High"
      ? "error"
      : note.Priority === "Medium"
        ? "warning"
        : "info";

  const myApproval =
    note.Status === "Pending Approval"
      ? (note.Approvals || []).find(
          (a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending",
        )
      : null;
  const openItems = (note.Items || []).filter((i) => i.OpenBase > 0);
  const canCancel =
    (note.Status === "Approved" || note.Status === "Partially Issued") && openItems.length > 0;

  const refresh = async () => {
    const fresh = await getDemandNote(note.Id);
    if (fresh) {
      setNote(fresh);
    }
    onChanged?.();
  };

  const decide = async (status) => {
    setBusy(true);
    try {
      const res = await decideDemandNote(myApproval.Id, status, comments);
      if (res.ok) {
        toast.success(`Demand note ${status.toLowerCase()}`);
        setComments("");
        await refresh();
      } else {
        toast.error(res.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const openCancel = () => {
    const defaults = {};
    openItems.forEach((i) => {
      defaults[i.id] = String(i.OpenBase);
    });
    setCancelQty(defaults);
    setCancelReason("");
    setShowCancel(true);
  };

  const submitCancel = async (all) => {
    if (!cancelReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    const over = all ? null : openItems.find((i) => Number(cancelQty[i.id] || 0) > i.OpenBase);
    if (over) {
      toast.error(
        `Cannot cancel more than the open quantity of ${over.MaterialName} (${over.OpenBase}).`,
      );
      return;
    }
    const lines = all
      ? []
      : openItems
          .map((i) => ({ lineId: i.id, quantity: Number(cancelQty[i.id] || 0) }))
          .filter((l) => l.quantity > 0);
    if (!all && lines.length === 0) {
      toast.error("Enter a quantity to cancel on at least one line");
      return;
    }
    setBusy(true);
    try {
      const res = await cancelDemandNoteRemainder(note.Id, cancelReason.trim(), lines);
      if (res.ok) {
        toast.success("Remainder cancelled");
        setShowCancel(false);
        await refresh();
      } else {
        toast.error(res.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" style={{ display: "block", zIndex: 1100 }}>
      <div
        className="modal-content-cat_voucher6"
        style={{ maxWidth: "850px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Header */}
        <div className="row">
          <div className="col-12 page_heading d-flex justify-content-between align-items-center">
            <h4 className="text-left pt-2 m-0" style={{ color: "#056363" }}>
              Demand Note: {note.DemandNoteNo}
            </h4>
            <span className="close pr-2" onClick={onClose} style={{ cursor: "pointer" }}>
              &times;
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="row p-3">
          {/* Summary Banner */}
          <div className="col-12 mb-3">
            <div
              className="d-flex flex-wrap justify-content-between align-items-center p-3"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "6px",
              }}
            >
              <div>
                <span className="text-muted mr-2" style={{ fontSize: "13px" }}>
                  Status:
                </span>
                <StatusChip
                  status={note.Status || "Pending Approval"}
                  type={statusChipType(note.Status || "Pending Approval")}
                />
              </div>
              <div>
                <span className="text-muted mr-2" style={{ fontSize: "13px" }}>
                  Priority:
                </span>
                <StatusChip status={note.Priority || "Medium"} type={priorityType} />
              </div>
              <div>
                <span className="text-muted mr-2" style={{ fontSize: "13px" }}>
                  Requisition Date:
                </span>
                <span style={{ fontWeight: 600 }}>{note.RequisitionDate || "N/A"}</span>
              </div>
              <div>
                <span className="text-muted mr-2" style={{ fontSize: "13px" }}>
                  Expected Delivery:
                </span>
                <span style={{ fontWeight: 600 }}>{note.ExpectedDeliveryDate || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Key Information */}
          <div className="col-md-6 mb-2">
            <p className="mb-1" style={{ fontSize: "13px" }}>
              <strong>Requesting Site:</strong> {note.RequestingSite || "N/A"}
            </p>
            <p className="mb-1" style={{ fontSize: "13px" }}>
              <strong>Requested By:</strong> {note.RequestedBy || "N/A"}
            </p>
          </div>
          <div className="col-md-6 mb-2">
            <p className="mb-1" style={{ fontSize: "13px" }}>
              <strong>Approvers:</strong> {note.FirstApprover || "N/A"}
            </p>
            <p className="mb-1" style={{ fontSize: "13px" }}>
              <strong>Procurement Alert:</strong>{" "}
              {note.ProcurementAlert ? (
                <span className="text-success font-weight-bold">
                  Enabled (Instant Notification)
                </span>
              ) : (
                <span className="text-muted">Disabled</span>
              )}
            </p>
          </div>

          {note.SpecificInstructions && (
            <div className="col-12 mt-2 mb-3">
              <div
                style={{
                  background: "#fff8e1",
                  border: "1px solid #ffe082",
                  borderRadius: "5px",
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: "#5d4037",
                }}
              >
                <strong>Specific Instructions:</strong> {note.SpecificInstructions}
              </div>
            </div>
          )}

          {note.CancelledReason && (
            <div className="col-12 mb-3" style={{ fontSize: "13px" }}>
              <strong>Cancelled Reason:</strong> {note.CancelledReason}
            </div>
          )}

          {note.FileName && (
            <div className="col-12 mb-3">
              <div className="d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                <strong>Attached Specification:</strong>
                <a
                  className="badge badge-light p-2 ml-2 border"
                  href={note.FileUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiDownload className="mr-1" /> {note.FileName}
                </a>
              </div>
            </div>
          )}

          {/* Required Materials Table */}
          <div className="col-12 mt-2">
            <h6 style={{ color: "#056363", fontWeight: 600, marginBottom: "10px" }}>
              Required Materials ({note.Items?.length || 0})
            </h6>
            <div className="table-responsive">
              <table className="table table-bordered table-hover" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr style={{ height: "38px" }}>
                    <th className="text-center" style={{ width: 50 }}>
                      Sr#
                    </th>
                    <th className="text-center">Material</th>
                    <th className="text-center">Specification / Brand</th>
                    <th className="text-center">Packaging</th>
                    <th className="text-center">Qty Required</th>
                    <th className="text-center">Unit</th>
                    <th className="text-center">Est. Stock</th>
                    <th className="text-center">Purpose / Location</th>
                  </tr>
                </thead>
                <tbody>
                  {(note.Items || []).map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {idx + 1}
                      </td>
                      <td style={{ verticalAlign: "middle", fontWeight: 600 }}>
                        {item.MaterialName || item.MaterialId}
                      </td>
                      <td style={{ verticalAlign: "middle" }}>{item.Specification || "—"}</td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {item.RequestedPackaging || "—"}
                      </td>
                      <td
                        className="text-center"
                        style={{ verticalAlign: "middle", fontWeight: 600 }}
                      >
                        {item.QtyRequired}
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {item.Unit || "—"}
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {item.EstStock || "—"}
                      </td>
                      <td style={{ verticalAlign: "middle" }}>{item.UsagePurpose || "—"}</td>
                    </tr>
                  ))}
                  {(!note.Items || note.Items.length === 0) && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-3">
                        No material line items recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Approvals */}
          <div className="col-12 mt-3">
            <h6 style={{ color: "#056363", fontWeight: 600, marginBottom: "10px" }}>Approvals</h6>
            <div className="table-responsive">
              <table className="table table-bordered" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr style={{ height: "38px" }}>
                    <th>Approver</th>
                    <th className="text-center">Status</th>
                    <th>Comments</th>
                    <th className="text-center">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(note.Approvals || []).map((a) => (
                    <tr key={a.Id}>
                      <td style={{ verticalAlign: "middle" }}>
                        {a.ApproverName || a.ApproverUserId}
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <StatusChip status={a.Status} type={statusChipType(a.Status)} />
                      </td>
                      <td style={{ verticalAlign: "middle" }}>{a.Comments || "—"}</td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {a.ActionDate ? String(a.ActionDate).slice(0, 16).replace("T", " ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-12 mt-1" style={{ fontSize: "13px" }}>
            <strong>Purchase Requisitions:</strong>{" "}
            {(note.PurchaseRequisitions || []).length === 0
              ? "None"
              : note.PurchaseRequisitions.map((p) => `${p.PrNumber} (${p.Status})`).join(", ")}
          </div>

          {/* Approve / Reject */}
          {myApproval && (
            <div className="col-12 mt-3">
              <label className="input_label">Comments:</label>
              <textarea
                className="input_styling"
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
              <div className="text-right mt-2">
                <ActionButton
                  text="Reject"
                  variant="cancel"
                  type="button"
                  isLoading={busy}
                  onClick={() => decide("Rejected")}
                />
                <ActionButton
                  text="Approve"
                  type="button"
                  isLoading={busy}
                  onClick={() => decide("Approved")}
                />
              </div>
            </div>
          )}

          {/* Cancel remainder */}
          {canCancel && showCancel && (
            <div
              className="col-12 mt-3 p-3"
              style={{ border: "1px solid #e9ecef", borderRadius: "6px" }}
            >
              <h6 style={{ color: "#056363", fontWeight: 600 }}>Cancel remainder</h6>
              <label className="input_label">Reason (required):</label>
              <input
                type="text"
                className="input_styling mb-2"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <table className="table table-bordered" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#E8FBFB" }}>
                  <tr>
                    <th>Material</th>
                    <th className="text-center">Open</th>
                    <th className="text-center">Quantity to cancel</th>
                  </tr>
                </thead>
                <tbody>
                  {openItems.map((i) => (
                    <tr key={i.id}>
                      <td>{i.MaterialName}</td>
                      <td className="text-center">
                        {i.OpenBase} {i.BaseUomName}
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          className="input_styling text-right"
                          min="0"
                          max={i.OpenBase}
                          step="any"
                          value={cancelQty[i.id] ?? ""}
                          onChange={(e) =>
                            setCancelQty((prev) => ({ ...prev, [i.id]: e.target.value }))
                          }
                        />{" "}
                        {i.BaseUomName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right">
                <ActionButton
                  text="Back"
                  variant="grey"
                  type="button"
                  onClick={() => setShowCancel(false)}
                />
                <ActionButton
                  text="Cancel all remaining"
                  variant="cancel"
                  type="button"
                  isLoading={busy}
                  onClick={() => submitCancel(true)}
                />
                <ActionButton
                  text="Cancel quantities"
                  type="button"
                  isLoading={busy}
                  onClick={() => submitCancel(false)}
                />
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div className="col-12 text-right mt-3">
            {canCancel && !showCancel && (
              <ActionButton
                text="Cancel remainder"
                variant="cancel"
                type="button"
                onClick={openCancel}
              />
            )}
            <ActionButton text="Close" variant="grey" type="button" onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemandNoteDetailsModal;
